/**
 * Shared auth-error classifier.
 *
 * Used by AuthContext (the UX auth guard) and AppContext (the API client) so
 * that "what counts as a real logout" is defined in exactly one place and
 * unit-tested once.
 *
 * Design rule: err toward KEEPING the session. Only errors that unambiguously
 * mean "this session is dead" are treated as definitive; everything else
 * (network blips, timeouts, unknown throws) is transient and must not tear
 * down a live session locally. This is safe because the authoritative logout
 * is the API-layer 401 handler in AppContext.fetchWithAuth() — if a session is
 * truly dead, the next real API call 401s and forces the logout. Keeping a
 * session alive locally can never grant access the backend would deny, since
 * every microservice validates the Bearer token independently.
 */

const DEFINITIVE_AUTH_ERROR_NAMES = new Set([
  "UserUnAuthenticatedException", // Amplify: no valid session in the token store
  "NotAuthorizedException", // Cognito: refresh token expired / revoked / invalid
]);

/** True only for errors that unambiguously mean "this session is dead → log out". */
export function isDefinitiveAuthError(error: unknown): boolean {
  const name = (error as { name?: string } | null | undefined)?.name;
  return !!name && DEFINITIVE_AUTH_ERROR_NAMES.has(name);
}

/** Everything else (network blips, timeouts, unknown) is treated as transient. */
export function isTransientError(error: unknown): boolean {
  return !isDefinitiveAuthError(error);
}
